'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import CategoryThumb from './CategoryThumb';
import BrandLogo from './BrandLogo';
import { useCurrency } from '@/app/context/CurrencyContext';
import { useRouter } from 'next/navigation';
import { HeartIcon, BellIcon, UserIcon, HomeIcon, ReceiptIcon, LogoutIcon, CartIcon, SearchIcon } from './icons';

interface Category {
  id: number;
  name: string;
  slug: string;
  icon_url: string | null;
  icon_fallback_url: string | null;
}

interface CurrentUser {
  id: number;
  name: string;
  email: string;
  privatePreview?: boolean;
}

interface NotificationItem {
  id: string;
  text: string;
  isRead: boolean;
  createdAt: string;
}

interface CartItem {
  cartId: number;
  productId: number;
  name: string;
  slug: string;
  variation: string | null;
  quantity: number;
  price: number;
  lineTotal: number;
  thumbnailUrl: string | null;
  thumbnailFallback: string | null;
}

function CartThumb({ item }: { item: CartItem }) {
  const [src, setSrc] = useState(item.thumbnailUrl);
  const [fallback, setFallback] = useState(item.thumbnailFallback);

  if (!src) return <div className="w-full h-full flex items-center justify-center text-lg bg-gray-50">💎</div>;

  return (
    <Image
      src={src}
      alt={item.name}
      fill
      sizes="48px"
      className="object-cover"
      onError={() => {
        if (fallback) {
          setFallback(null);
          setSrc(fallback);
        } else {
          setSrc(null);
        }
      }}
    />
  );
}

export default function Header() {
  const router = useRouter();
  const { currencies, selectedCurrency, changeCurrency, formatPrice } = useCurrency();
  const [showCategories, setShowCategories] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [showCartPreview, setShowCartPreview] = useState(false);
  const cartSubtotal = cartItems.reduce((sum, item) => sum + item.lineTotal, 0);

  useEffect(() => {
    fetch('/api/auth/me')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setCurrentUser(data?.user ? { ...data.user, privatePreview: data.privatePreview === true } : null))
      .catch(() => undefined)
      .finally(() => setAuthLoading(false));
  }, []);

  // Notifications only exist for a signed-in customer, so this waits for the
  // session check above rather than firing for every visitor.
  useEffect(() => {
    if (!currentUser) return;
    fetch('/api/notifications')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        setNotifications(data?.notifications || []);
        setUnreadCount(data?.unreadCount || 0);
      })
      .catch(() => undefined);
  }, [currentUser]);

  // Wishlist count only exists for a signed-in customer, same as notifications.
  useEffect(() => {
    if (!currentUser) {
      setWishlistCount(0);
      return;
    }
    fetch('/api/wishlist')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setWishlistCount(data?.count || 0))
      .catch(() => undefined);
  }, [currentUser]);

  // Cart preview mirrors the wishlist/notifications pattern - only loaded
  // once we know there's a signed-in customer to load it for. Also
  // re-fetches on a "cart:updated" window event so pages like the product
  // detail page's real Add to Cart button can refresh the header pill
  // without a full reload.
  useEffect(() => {
    if (!currentUser) {
      setCartItems([]);
      return;
    }
    const refreshCart = () => {
      fetch('/api/cart')
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => setCartItems(data?.items || []))
        .catch(() => undefined);
    };
    refreshCart();
    window.addEventListener('cart:updated', refreshCart);
    return () => window.removeEventListener('cart:updated', refreshCart);
  }, [currentUser]);

  const removeCartItem = async (cartId: number) => {
    setCartItems((prev) => prev.filter((i) => i.cartId !== cartId));
    try {
      const res = await fetch('/api/cart', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cartId }),
      });
      if (!res.ok) throw new Error('remove failed');
    } catch {
      // Re-sync with the server if the delete didn't actually go through.
      fetch('/api/cart')
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => setCartItems(data?.items || []))
        .catch(() => undefined);
    }
  };

  const handleLogout = async () => {
    await fetch('/api/auth/logout', { method: 'POST' });
    setCurrentUser(null);
    router.refresh();
  };

  // Opening the bell marks everything read, matching the Laravel header's
  // nonLinkableNotificationRead() behaviour.
  const handleOpenNotifications = () => {
    setShowNotifications((prev) => !prev);
    if (unreadCount > 0) {
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      fetch('/api/notifications/mark-read', { method: 'POST' }).catch(() => undefined);
    }
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<{ keywords: string[]; products: any[] }>({ keywords: [], products: [] });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [photoCache, setPhotoCache] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSuggestions({ keywords: [], products: [] });
      return;
    }

    const delayDebounce = setTimeout(() => {
      fetch(`/api/search/suggestions?q=${encodeURIComponent(searchQuery)}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data && data.success) {
            setSuggestions({
              keywords: data.keywords || [],
              products: data.products || [],
            });
            
            const unresolvedIds = (data.products || [])
              .map((p: any) => p.thumbnail_img)
              .filter((id: string) => id && !photoCache[id]);
            
            if (unresolvedIds.length > 0) {
              unresolvedIds.forEach(async (id: string) => {
                try {
                  const pRes = await fetch(`/api/photos/${id}`);
                  if (pRes.ok) {
                    const pData = await pRes.json();
                    if (pData.url) {
                      setPhotoCache((prev) => ({ ...prev, [id]: pData.url }));
                    }
                  }
                } catch (e) {
                  console.error('Error fetching suggestion photo:', e);
                }
              });
            }
          }
        })
        .catch((err) => console.error('Error loading suggestions:', err));
    }, 150);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  useEffect(() => {
    fetch('/api/categories')
      .then((res) => res.json())
      .then((data) => setCategories(data.data || []))
      .catch((err) => console.error('Error fetching categories:', err));
  }, []);

  return (
    <>
      {currentUser?.privatePreview && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-amber-100 px-4 py-3 text-sm text-amber-950" role="status">
          <span><strong>Private customer preview</strong> — you are viewing the storefront as {currentUser.name}. Activity here uses this customer account.</span>
          <form method="post" action="/api/auth/private-login/exit">
            <button type="submit" className="rounded-md bg-gray-900 px-4 py-2 font-semibold text-white hover:bg-gray-700">Return to Business</button>
          </form>
        </div>
      )}
      {/* Top Info Bar - hidden through tablet widths too; only true desktop
          gets the full header, tablets keep the mobile floating nav so the
          nav row here doesn't get cramped into a width it wasn't built for */}
      <div className="hidden lg:block bg-[var(--banner-bg)] border-b border-[var(--border-color)] py-2 px-6">
        <div className="w-full flex items-center justify-between flex-wrap gap-3 text-xs sm:text-sm text-gray-700">
          <span className="text-gray-900 font-medium">Trusted Wholesale Jewelry Manufacturer for Retail</span>
          <div className="flex items-center gap-3 flex-wrap">
            <a
              href="https://api.whatsapp.com/send?phone=918003816293"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[var(--orange-hover)] transition flex items-center gap-1"
            >
              <span>📞</span> WhatsApp/Call +91 8003816293
            </a>
            <span className="text-gray-500">|</span>
            <a href="mailto:sales@marothjewels.com" className="hover:text-[var(--orange-hover)] transition flex items-center gap-1">
              <span>📧</span> Wholesale Inquiry: sales@marothjewels.com
            </a>
            <span className="text-gray-500">|</span>
            <span className="flex items-center gap-1">🌍 Worldwide Shipping Available</span>
            <span className="text-gray-500">|</span>
            <select
              aria-label="Currency"
              value={selectedCurrency?.code || 'USD'}
              onChange={(e) => changeCurrency(e.target.value)}
              className="bg-transparent border-0 text-gray-700 hover:text-[var(--orange-hover)] cursor-pointer text-xs font-semibold focus:outline-none"
            >
              {currencies.map((curr) => (
                <option key={curr.code} value={curr.code}>
                  {curr.name} ({curr.symbol})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Header */}
      {/* No header at all below lg - the floating bottom nav (with the
          brand mark as its Home icon) replaces it through phone AND tablet
          widths; this full row is built for desktop-width space only. */}
      <header className="hidden lg:block bg-[var(--header-bg)] backdrop-blur-md border-b border-[var(--border-color)] sticky top-0 z-50">
        <div className="w-full px-6 py-3">
          <div className="w-full flex items-center justify-center sm:justify-between gap-4 mb-3">
            <div className="flex items-center flex-shrink-0">
              <BrandLogo />
            </div>

            {/* Search Bar - Full Width with live suggestions dropdown */}
            <div className="flex-1 hidden lg:block mx-4 relative">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (searchQuery.trim()) {
                    setShowSuggestions(false);
                    router.push(`/search?keyword=${encodeURIComponent(searchQuery)}`);
                  }
                }}
                className="w-full flex items-center relative"
              >
                <input
                  type="text"
                  placeholder="Search Wholesale Jewelry Designs"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  className="w-full px-4 py-2.5 pr-10 border border-[var(--border-color)] bg-white rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[var(--orange-primary)]"
                />
                <button
                  type="submit"
                  aria-label="Search"
                  className="absolute right-1.5 w-7 h-7 rounded-full bg-orange-50 text-[var(--orange-hover)] hover:bg-orange-100 hover:text-[var(--orange-dark)] transition flex items-center justify-center"
                >
                  <SearchIcon className="w-3.5 h-3.5" />
                </button>
              </form>

              {/* Suggestions Panel Dropdown matching screenshot */}
              {showSuggestions && searchQuery.trim().length >= 2 && (
                <>
                  {/* Click outside overlay */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowSuggestions(false)}
                  />
                  
                  <div className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-[var(--border-color)] z-50 py-4 max-h-[80vh] overflow-y-auto select-none">
                    
                    {/* 1. Popular Suggestions Section */}
                    {suggestions.keywords.length > 0 && (
                      <div className="mb-4">
                        <div className="px-4 py-1 text-[10px] font-bold text-gray-500 uppercase tracking-widest bg-gray-50/50 mb-1">
                          Popular Suggestions
                        </div>
                        <div className="space-y-0.5">
                          {suggestions.keywords.map((kw) => (
                            <button
                              key={kw}
                              type="button"
                              onClick={() => {
                                setSearchQuery(kw);
                                setShowSuggestions(false);
                                router.push(`/search?keyword=${encodeURIComponent(kw)}`);
                              }}
                              className="w-full text-left px-4 py-2 text-xs font-semibold text-gray-800 hover:bg-gray-50 hover:text-[var(--orange-hover)] transition"
                            >
                              {kw}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 2. Products Section */}
                    {suggestions.products.length > 0 && (
                      <div>
                        <div className="px-4 py-1 text-[10px] font-bold text-gray-500 uppercase tracking-widest bg-gray-50/50 mb-1">
                          Products
                        </div>
                        <div className="divide-y divide-gray-50">
                          {suggestions.products.map((p) => {
                            const discountedPrice = p.discount > 0
                              ? p.discount_type === 'amount'
                                ? Math.max(0, p.unit_price - p.discount)
                                : Math.max(0, p.unit_price - (p.unit_price * p.discount) / 100)
                              : p.unit_price;

                            const thumbSrc = p.thumbnail_img ? photoCache[p.thumbnail_img] : null;

                            return (
                              <Link
                                key={p.id}
                                href={`/product/${p.slug}`}
                                onClick={() => setShowSuggestions(false)}
                                className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition"
                              >
                                {/* Mini Product Thumbnail Image */}
                                <div className="relative w-10 h-10 rounded border border-gray-100 bg-gray-50 overflow-hidden flex items-center justify-center shrink-0">
                                  {thumbSrc ? (
                                    <Image
                                      src={thumbSrc}
                                      alt={p.name}
                                      fill
                                      sizes="40px"
                                      className="object-cover"
                                    />
                                  ) : (
                                    <span className="text-xl">💎</span>
                                  )}
                                </div>
                                
                                {/* Product details */}
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-xs font-bold text-gray-800 line-clamp-1">
                                    {p.name}
                                  </h4>
                                  <div className="flex items-baseline gap-2 mt-0.5">
                                    <span className="text-[var(--orange-dark)] font-bold text-xs">
                                      {formatPrice(discountedPrice)}
                                    </span>
                                    {p.discount > 0 && (
                                      <span className="text-gray-500 line-through text-[9px]">
                                        {formatPrice(p.unit_price)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {suggestions.keywords.length === 0 && suggestions.products.length === 0 && (
                      <div className="text-center py-6 text-gray-500 text-xs">
                        No matches found.
                      </div>
                    )}

                  </div>
                </>
              )}
            </div>

            {/* Right Icons - the row is fully hidden on mobile, not just its
                children, so it doesn't reserve any empty space there */}
            <div className="hidden lg:flex items-center gap-3 min-w-0">
               
               {/* Wishlist heart - count matches get_wishlists()->count() on
                   the live site, so the badge stays in sync with what the
                   customer would see there. */}
               {currentUser && (
                 <Link
                   href="/wishlists"
                   className="relative bg-orange-50 text-[var(--orange-hover)] hover:bg-orange-100 hover:text-[var(--orange-dark)] transition flex items-center justify-center w-8 h-8 rounded-full"
                   title="Wishlist"
                 >
                   <HeartIcon className="w-4 h-4" />
                   {wishlistCount > 0 && (
                     <span className="absolute -top-1 -right-1 bg-[var(--orange-primary)] text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                       {wishlistCount > 9 ? '9+' : wishlistCount}
                     </span>
                   )}
                 </Link>
               )}

               {/* Notification bell - order-status updates for the signed-in
                   customer, sourced from the same notifications table the
                   Laravel header reads. Hidden while signed out, since
                   nothing in that table ever targets a guest. */}
               {currentUser && (
                 <div className="relative">
                   <button
                     onClick={handleOpenNotifications}
                     className="relative bg-orange-50 text-[var(--orange-hover)] hover:bg-orange-100 hover:text-[var(--orange-dark)] transition flex items-center justify-center w-8 h-8 rounded-full"
                     title="Notifications"
                   >
                     <BellIcon className="w-4 h-4" />
                     {unreadCount > 0 && (
                       <span className="absolute -top-1 -right-1 bg-[var(--orange-primary)] text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                         {unreadCount > 9 ? '9+' : unreadCount}
                       </span>
                     )}
                   </button>

                   {showNotifications && (
                     <>
                       <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
                       <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-[var(--border-color)] z-50 py-3">
                         <h4 className="px-4 pb-2 mb-1 text-sm font-bold text-gray-900 border-b border-gray-100">
                           Notifications
                         </h4>
                         <div className="max-h-80 overflow-y-auto">
                           {notifications.length === 0 ? (
                             <div className="text-center py-8 text-sm text-gray-500">No notification found</div>
                           ) : (
                             notifications.map((n) => (
                               <div key={n.id} className="px-4 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0">
                                 <p className="text-xs text-gray-700 leading-snug">{n.text}</p>
                                 <span className="text-[10px] text-gray-500">
                                   {new Date(n.createdAt).toLocaleDateString('en-GB', {
                                     day: 'numeric',
                                     month: 'short',
                                     year: 'numeric',
                                   })}
                                 </span>
                               </div>
                             ))
                           )}
                         </div>
                       </div>
                     </>
                   )}
                 </div>
               )}

               {/* Account - Login/Register when signed out; once
                   /api/auth/me resolves a session, avatar + name opens the
                   Dashboard/Purchase History/Conversations/Logout menu
                   matching the live site's account dropdown. */}
               <div className="hidden lg:flex items-center gap-1.5 select-none relative">
                 {authLoading ? null : currentUser ? (
                   <>
                     <button
                       onClick={() => setShowAccountMenu((prev) => !prev)}
                       className="flex items-center gap-1.5"
                     >
                       <span
                         className="text-[var(--orange-hover)] flex items-center justify-center w-8 h-8 rounded-full bg-orange-50 text-xs font-bold"
                         title={currentUser.email}
                       >
                         {currentUser.name.charAt(0).toUpperCase()}
                       </span>
                       <span className="hidden md:inline text-[11px] font-semibold text-gray-700 max-w-[90px] truncate">
                         {currentUser.name}
                       </span>
                     </button>

                     {showAccountMenu && (
                       <>
                         <div className="fixed inset-0 z-40" onClick={() => setShowAccountMenu(false)} />
                         <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-2xl border border-[var(--border-color)] z-50 py-2">
                           <Link
                             href="/dashboard"
                             className="flex items-center gap-2.5 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-orange-50 hover:text-[var(--orange-hover)] transition"
                           >
                             <HomeIcon className="w-4 h-4 shrink-0" /> Dashboard
                           </Link>
                           <Link
                             href="/purchase_history"
                             className="flex items-center gap-2.5 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-orange-50 hover:text-[var(--orange-hover)] transition"
                           >
                             <ReceiptIcon className="w-4 h-4 shrink-0" /> Purchase History
                           </Link>
                           <div className="border-t border-gray-100 my-1" />
                           <button
                             onClick={handleLogout}
                             className="w-full flex items-center gap-2.5 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-orange-50 hover:text-[var(--orange-hover)] transition text-left"
                           >
                             <LogoutIcon className="w-4 h-4 shrink-0" /> Logout
                           </button>
                         </div>
                       </>
                     )}
                   </>
                 ) : (
                   <>
                     <Link
                       href="/login"
                       className="bg-orange-50 text-[var(--orange-hover)] hover:bg-orange-100 hover:text-[var(--orange-dark)] transition flex items-center justify-center w-8 h-8 rounded-full"
                       title="Login"
                     >
                       <UserIcon className="w-4 h-4" />
                     </Link>
                     <Link
                       href="/register"
                       className="hidden md:inline-block text-[11px] font-semibold text-gray-500 hover:text-[var(--orange-hover)] transition"
                     >
                       Registration
                     </Link>
                   </>
                 )}
               </div>
               
             </div>
           </div>

           {/* Navigation Bar - cart button sits at the trailing end of this
               row (screenshot reference), separate from the top icon row. */}
           <div className="w-full hidden lg:block">
            <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-[var(--border-color)] pt-3">
              <Link href="/" className="text-gray-700 hover:text-[var(--orange-hover)] hover:bg-orange-100 transition font-medium text-sm whitespace-nowrap px-3 py-1.5 rounded-full">
                Home
              </Link>

              {/* Categories Mega Menu */}
              <div
                className="relative"
                onMouseEnter={() => setShowCategories(true)}
                onMouseLeave={() => setShowCategories(false)}
              >
                <button
                  onClick={() => setShowCategories(!showCategories)}
                  className={`transition font-medium text-sm whitespace-nowrap flex items-center gap-1 px-3 py-1.5 rounded-full ${
                    showCategories
                      ? 'bg-orange-100 text-[var(--orange-hover)]'
                      : 'text-gray-700 hover:text-[var(--orange-hover)] hover:bg-orange-100'
                  }`}
                >
                  Categories
                  <span className={`transition-transform ${showCategories ? 'rotate-180' : ''}`}>▼</span>
                </button>

                {/* Half-width mega menu. Sits flush under the button so moving the
                    mouse down never crosses a gap and closes it. */}
                {showCategories && (
                  <div className="absolute left-0 top-full w-[92vw] lg:w-[68vw] max-w-6xl bg-white rounded-xl shadow-2xl p-6 z-50 border border-[var(--border-color)]">
                    <div className="grid grid-cols-2 lg:grid-cols-4 lg:divide-x divide-[var(--border-color)]">
                      {[0, 1, 2, 3].map((col) => (
                        <div key={col} className="px-4 space-y-2">
                          {categories
                            // Column height follows the list length so no category is dropped
                            .filter((_, i) => Math.floor(i / Math.ceil(categories.length / 4)) === col)
                            .map((cat) => (
                              <Link
                                key={cat.slug}
                                href={`/category/${cat.slug}`}
                                className="flex items-center gap-4 p-3 rounded-lg hover:bg-orange-50/60 hover:shadow-md transition group/item"
                              >
                                <CategoryThumb
                                  src={cat.icon_url}
                                  fallbackSrc={cat.icon_fallback_url}
                                  alt={cat.name}
                                  className="w-14 h-14 rounded-full bg-gray-50 shrink-0"
                                />
                                <span className="text-[15px] font-medium text-gray-800 group-hover/item:text-[var(--orange-hover)] leading-tight">
                                  {cat.name}
                                </span>
                              </Link>
                            ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link href="/todays-deal" className="text-gray-700 hover:text-[var(--orange-hover)] hover:bg-orange-100 transition font-medium text-sm whitespace-nowrap px-3 py-1.5 rounded-full">
                Today's Deal
              </Link>
              <Link href="/blog" className="text-gray-700 hover:text-[var(--orange-hover)] hover:bg-orange-100 transition font-medium text-sm whitespace-nowrap px-3 py-1.5 rounded-full">
                Blogs
              </Link>
              <Link href="/collections" className="text-gray-700 hover:text-[var(--orange-hover)] hover:bg-orange-100 transition font-medium text-sm whitespace-nowrap px-3 py-1.5 rounded-full">
                Our Collection
              </Link>
              <Link href="/custom-jewelry-request" className="text-gray-700 hover:text-[var(--orange-hover)] hover:bg-orange-100 transition font-medium text-sm whitespace-nowrap px-3 py-1.5 rounded-full">
                Custom Request
              </Link>
              <Link href="/about" className="text-gray-700 hover:text-[var(--orange-hover)] hover:bg-orange-100 transition font-medium text-sm whitespace-nowrap px-3 py-1.5 rounded-full">
                About Us
              </Link>

              {/* Cart - pushed to the trailing end of the nav row via
                  ml-auto, matching the live site's screenshot layout. */}
              <div className="relative ml-auto">
                <button
                  onClick={() => setShowCartPreview((prev) => !prev)}
                  className="flex items-center gap-2 border border-[var(--border-color)] rounded-full px-3 py-1.5 hover:bg-gray-50 transition text-gray-700 select-none"
                >
                  <CartIcon className="w-4 h-4 text-[var(--orange-hover)]" />
                  <div className="flex items-center gap-1 text-[11px] leading-none">
                    <span className="font-bold text-gray-900">{formatPrice(cartSubtotal)}</span>
                    <span className="text-gray-500">
                      ({cartItems.length} {cartItems.length === 1 ? 'Item' : 'Items'})
                    </span>
                  </div>
                </button>

                {showCartPreview && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowCartPreview(false)} />
                    <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-[var(--border-color)] z-50 py-3">
                      <h4 className="px-4 pb-2 mb-1 text-sm font-bold text-gray-900 border-b border-gray-100">
                        Cart Items
                      </h4>

                      {cartItems.length === 0 ? (
                        <div className="text-center py-8 text-sm text-gray-500">Your cart is empty</div>
                      ) : (
                        <>
                          <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
                            {cartItems.map((item) => (
                              <div key={item.cartId} className="px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50">
                                <Link
                                  href={`/product/${item.slug}`}
                                  className="relative w-12 h-12 rounded-lg overflow-hidden shrink-0 border border-[var(--border-color)]"
                                >
                                  <CartThumb item={item} />
                                </Link>
                                <div className="min-w-0 flex-1">
                                  <Link
                                    href={`/product/${item.slug}`}
                                    className="text-xs font-semibold text-gray-800 hover:text-[var(--orange-hover)] transition line-clamp-1"
                                  >
                                    {item.name}
                                  </Link>
                                  {item.variation && (
                                    <p className="text-[10px] text-gray-500 line-clamp-1">Variation: {item.variation}</p>
                                  )}
                                  <p className="text-[11px] text-gray-500 mt-0.5">
                                    {item.quantity}x {formatPrice(item.price)}
                                  </p>
                                </div>
                                <button
                                  onClick={() => removeCartItem(item.cartId)}
                                  className="text-gray-500 hover:text-red-500 transition text-sm shrink-0"
                                  title="Remove"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>

                          <div className="flex items-center justify-between px-4 pt-3 mt-1 border-t border-gray-100">
                            <span className="text-xs font-semibold text-gray-600">Subtotal</span>
                            <span className="text-sm font-bold text-gray-900">{formatPrice(cartSubtotal)}</span>
                          </div>

                          <div className="px-4 pt-3">
                            <Link
                              href="/cart"
                              onClick={() => setShowCartPreview(false)}
                              className="block text-center w-full bg-[var(--orange-primary)] hover:bg-[var(--orange-hover)] text-white py-2 rounded-full font-semibold text-xs transition"
                            >
                              View Cart
                            </Link>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </nav>
          </div>
        </div>
      </header>
    </>
  );
}
